import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { Button } from '@deliveryhero/pd-cookbook/components/ButtonV2';
import IcEdit from '@deliveryhero/pd-cookbook/components/Icons/IcEdit';
import IcTrash from '@deliveryhero/pd-cookbook/components/Icons/IcTrash';
import { MenuItem } from '@mui/material';

import ActionMenu from 'components/ActionMenu';
import CallToActions from 'components/CallToActions';
import { LoadingAnimation } from 'components/LoadingAnimation';
import Page, { ActionType } from 'components/Page';
import PageHeader from 'components/PageHeader/PageHeader';
import TableList from 'components/TableList';
import { Column } from 'components/TableList/SortableHeader';
import { RowsPerPage } from 'components/TableList/types';
import { useConfiguration } from 'contexts/country-config';
import { FormikHelpers, FormikProps } from 'formik';
import useAction from 'hooks/useAction';
import { ActionOptionType } from 'hooks/useAction/useAction';
import useApi from 'hooks/useApi';
import useNotification from 'hooks/useNotification/useNotification';
import useQueryParam from 'hooks/useQueryParam/useQueryParam';
import useQueryWrapperP0 from 'hooks/useQueryWrapperP0';
import useQueryWrapperP1 from 'hooks/useQueryWrapperP1';
import { Department, DepartmentPayload } from 'services/api';
import { dispatchGaEvent, PageType } from 'services/google-analytics';
import { AppRoute } from 'share/types/routes';
import CorproateIcon from 'src/components/svg/CorporateIcon';
import FoodoraDepartment from 'src/components/svg/FoodoraDepartment';
import DepartmentQueryKeys from 'src/query-keys/department';
import isFoodora from 'src/utils/is-foodora/is-foodora';
import generateQuery from 'utils/generate-query';

import {
  Actions,
  add_department_button,
  add_department_title,
  delete_department_button,
  delete_department_dialog_confirmation_button,
  delete_department_dialog_confirmation_subtitle,
  delete_department_dialog_confirmation_title,
  delete_department_dialog_return_button,
  department_table_header_two,
  edit_department_button,
  edit_department_header,
  empty_page_subtitle,
  empty_page_title,
  right_drawer_button_save,
  right_drawer_button_save_and_continue,
  subtitle,
  title
} from './constants';
import { DepartmentFilters } from './Filter/DepartmentFilters';
import DepartmentForm, { DepartmentValuesType } from './Form/DepartmentForm';

export function DepartmentContainer() {
  const api = useApi();
  const navigate = useNavigate();
  const countryConfig = useConfiguration();
  const queryParam = useQueryParam();
  const queryClient = useQueryClient();
  const [, setAction, { id: id_str }] = useAction<Actions>();
  const id = parseInt(id_str);
  const filters = ['search', 'count', 'offset'];
  const [departmentName, setDepartmentName] = useState('');
  const [keepSidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    dispatchGaEvent({ pageType: PageType.DEPARMENT });
  }, []);

  const filtered = Object.keys(queryParam)
    .filter((key) => filters.includes(key))
    .reduce((param: Record<string, string>, key) => {
      param[key] = queryParam[key];
      return param;
    }, {});

  // queries
  const departments = useQueryWrapperP0(
    DepartmentQueryKeys.list(filtered),
    async () => {
      return api.company.department.list(filtered);
    }
  );

  const department = useQueryWrapperP1(
    DepartmentQueryKeys.detail(id),
    async () => {
      const response = await api.company.department.get(id);
      return response.data;
    },
    {
      enabled: Boolean(id)
    }
  );

  const page = queryParam.offset ? parseInt(queryParam.offset) : undefined;
  const rowsPerPage = queryParam.count ? parseInt(queryParam.count) : undefined;
  const tableListProps = {
    ...(page && {
      page: Math.floor(page / (rowsPerPage || RowsPerPage.TWENTYFIVE))
    }),
    ...(rowsPerPage && { rowsPerPage })
  };

  const columns: Column<Department>[] = useMemo(
    () => [
      {
        id: 'name',
        options: { disablePadding: false },
        label: department_table_header_two
      }
      // Removed
      // {
      //   id: 'total_users',
      //   options: { disablePadding: false },
      //   label: department_table_header_three
      // }
    ],
    [countryConfig.timezone]
  );

  const handlePageChange = (
    changes: Record<string, string | number | undefined>
  ) => {
    let params = {
      ...queryParam,
      ...changes
    };

    params = Object.keys(params).reduce((acc, key) => {
      params[key] !== undefined && (acc[key] = params[key]);
      return acc;
    }, {} as Record<string, any>);

    navigate(
      {
        pathname: AppRoute.Department,
        search: generateQuery(params)
      },
      { replace: true }
    );
  };

  const handlePaginationChange = (page: number, rowsPerPage: number) => {
    handlePageChange({
      ...{
        offset: page ? page * rowsPerPage : undefined,
        count: rowsPerPage
      }
    });
  };

  const { pushNotification, dismissAllNotifications, dismissNotification } =
    useNotification();

  const handleClick = (action: Actions, options?: ActionOptionType) => {
    setAction(action, true, options);
  };

  // mutations
  const addDepartment = useMutation((payload: DepartmentPayload) =>
    api.company.department.create(payload)
  );

  const editDepartment = useMutation(
    (department: { id: number; payload: DepartmentPayload }) =>
      api.company.department.update(department.id, department.payload)
  );

  const deleteDepartment = useMutation((department: { id: number }) =>
    api.company.department.delete(department.id)
  );

  const handleSubmitEdit = (
    values: DepartmentValuesType,
    actions: FormikHelpers<DepartmentValuesType>
  ) => {
    if (id) {
      const snackbarKey = pushNotification(`Updating ${values.name}...`, {
        variant: 'info'
      });
      editDepartment.mutate(
        {
          id: id,
          payload: values as DepartmentValuesType
        },
        {
          onSuccess: () => {
            dismissNotification(snackbarKey);
            queryClient
              .invalidateQueries(DepartmentQueryKeys.list())
              .then(() => {
                pushNotification(`${values.name} updated`, {
                  variant: 'success'
                });
                setAction(false);
              });
          },
          onError: (e) => {
            const error = e as Error;
            const error_messge_id = pushNotification(
              `Error updating ${values.name}, ${error.message}`,
              {
                variant: 'error',
                onClick: () => {
                  actions.submitForm();
                  dismissNotification(error_messge_id);
                },
                autoHideDuration: 10000
              }
            );

            dismissNotification(snackbarKey);
          },
          onSettled: () => {
            actions.setSubmitting(false);
          }
        }
      );
    }
  };

  const handleSubmitDelete = () => {
    const departmentData = department.data;
    const snackbarKey = pushNotification(
      `Deleting ${departmentData?.name}...`,
      {
        variant: 'info'
      }
    );

    deleteDepartment.mutate(
      {
        id: id
      },
      {
        onSuccess: () => {
          dismissNotification(snackbarKey);
          queryClient.invalidateQueries(DepartmentQueryKeys.list()).then(() => {
            pushNotification(`${departmentName} deleted`, {
              variant: 'success'
            });
            setAction(false);
          });
        },
        onError: (e) => {
          dismissNotification(snackbarKey);

          const error = e as Error;
          pushNotification(
            `We are unable to delete ${departmentName}, ${error.message}`,
            {
              variant: 'error',
              onClick: () => dismissAllNotifications()
            }
          );
        }
      }
    );
  };

  const handleSubmitAdd = (
    values: DepartmentValuesType,
    actions: FormikHelpers<DepartmentValuesType>
  ) => {
    const snackbarKey = pushNotification(`Adding ${values.name}...`, {
      variant: 'info'
    });
    addDepartment.mutate(values, {
      onSuccess: () => {
        dismissNotification(snackbarKey);
        queryClient.invalidateQueries(DepartmentQueryKeys.list()).then(() => {
          pushNotification(`${values.name} added`, {
            variant: 'success'
          });
          setAction(Actions.Add, keepSidebarOpen);
          actions.resetForm();
        });
      },
      onError: (e) => {
        const error = e as Error;
        const error_messge_id = pushNotification(
          `Error adding ${values.name}, ${error.message}`,
          {
            variant: 'error',
            onClick: () => {
              actions.submitForm();
              dismissNotification(error_messge_id);
            },
            autoHideDuration: 10000
          }
        );

        dismissNotification(snackbarKey);
      },
      onSettled: () => {
        actions.setSubmitting(false);
      }
    });
  };

  const handleDelete = (row: Department) => {
    setDepartmentName(row.name);
    handleClick(Actions.Delete, { id: row.id.toString() });
  };

  const DeleteButtons = () => (
    <>
      <Button variant="secondary">
        {delete_department_dialog_return_button}
      </Button>
      <Button onClick={() => handleSubmitDelete()} marginLeft="sm">
        {delete_department_dialog_confirmation_button}
      </Button>
    </>
  );

  return (
    <Page
      actions={{
        [Actions.Delete]: {
          component: <DeleteButtons />,
          type: ActionType.AppDialog,
          options: {
            title: `${delete_department_dialog_confirmation_title} ${departmentName}?`,
            content: delete_department_dialog_confirmation_subtitle
          }
        },
        [Actions.Edit]: {
          component: !department.isLoading ? (
            <DepartmentForm
              initialValues={
                {
                  name: department.data?.name || ''
                } as DepartmentValuesType
              }
              handleSubmit={handleSubmitEdit}
              actions={({ values }: FormikProps<DepartmentValuesType>) => (
                <div style={{ width: '100%' }}>
                  <Button
                    key="1"
                    type="submit"
                    size="small"
                    layout="full-width-primary"
                    disabled={values.name.length === 0}
                  >
                    {right_drawer_button_save}
                  </Button>
                </div>
              )}
            />
          ) : (
            <LoadingAnimation />
          ),
          type: ActionType.RightDrawer,
          options: {
            title: edit_department_header
          }
        },
        [Actions.Add]: {
          component: (
            <DepartmentForm
              initialValues={
                {
                  name: ''
                } as DepartmentValuesType
              }
              handleSubmit={handleSubmitAdd}
              actions={({ values }: FormikProps<DepartmentValuesType>) => (
                <>
                  <Button
                    key="1"
                    disabled={values.name.length === 0}
                    size="small"
                    style={{ width: '160px' }}
                    type="submit"
                    variant="secondary"
                    onClick={() => {
                      setSidebarOpen(true);
                    }}
                  >
                    {right_drawer_button_save_and_continue}
                  </Button>
                  <Button
                    key="2"
                    disabled={values.name.length === 0}
                    size="small"
                    style={{ width: '160px' }}
                    type="submit"
                    onClick={() => {
                      setSidebarOpen(false);
                    }}
                  >
                    {right_drawer_button_save}
                  </Button>
                </>
              )}
            />
          ),
          type: ActionType.RightDrawer,
          options: {
            title: add_department_title
          }
        }
      }}
    >
      <PageHeader
        title={title}
        subtitle={<>{subtitle}</>}
        buttons={[
          <Button
            onClick={() => handleClick(Actions.Add)}
            key={'add-department'}
          >
            {add_department_button}
          </Button>
        ]}
      />
      <TableList
        total={
          departments.error ? 0 : departments.data ? departments.data.total : -1
        }
        columns={columns}
        loading={departments.isLoading}
        data={departments.data?.data || []}
        onPaginationChange={handlePaginationChange}
        expandFilters={true}
        {...tableListProps}
        empty={
          <CallToActions
            illustration={isFoodora() ? FoodoraDepartment : CorproateIcon}
            title={empty_page_title}
            subtitle={empty_page_subtitle}
          ></CallToActions>
        }
        action={(row: Department) => (
          <ActionMenu>
            <MenuItem
              onClick={() =>
                handleClick(Actions.Edit, { id: row.id.toString() })
              }
              style={{ minWidth: 120, display: 'flex', textAlign: 'left' }}
            >
              <IcEdit style={{ marginRight: '15px' }} />
              {edit_department_button}
            </MenuItem>
            <MenuItem
              onClick={() => handleDelete(row)}
              style={{ minWidth: 120, display: 'flex', textAlign: 'left' }}
            >
              <IcTrash style={{ marginRight: '15px' }} />
              {delete_department_button}
            </MenuItem>
          </ActionMenu>
        )}
      >
        <DepartmentFilters
          params={queryParam}
          onPageChange={handlePageChange}
        />
      </TableList>
    </Page>
  );
}
